import React, { useCallback, useMemo } from 'react';
import { Provider } from 'react-redux';
import { componentTypes } from '@white-nl/instantsearch-components';

import Usps from '../../components/result/Usps';
import store from '../../redux-modules/store';
import {
  useGetSessionInfoQuery,
  useGetWishlistQuery,
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
} from '../../redux-modules/service';

export type ProductHitProps = {
  title: string,
  url: string,
  image: {
    transforms: {
      productCatalogImage: {
        url: string,
        width: number,
        height: number,
      },
      productCatalogImage2x: {
        url: string,
        width: number,
        height: number,
      },
    },
  },
  priceFormatted: string,
  salePriceFormatted: string,
  isOnSale: boolean,
  isNew: boolean,
  isOutOfStock: boolean,
  productCode: string,
  defaultPrice: number,
  listId: string | number,
  listName: string,
  isInWishlist: boolean,
  onWishlistClick: () => void,
  onVisible: () => void,
};

function HitInner({
  results,
  hit,
  additionalData,
  component: Component,
}: componentTypes.HitProps & { component: React.ComponentType<ProductHitProps> }) {
  const { data: sessionInfo } = useGetSessionInfoQuery({});
  const { data: wishlist, isLoading: isWishlistLoading } = useGetWishlistQuery(undefined);
  const [addToWishlist] = useAddToWishlistMutation();
  const [removeFromWishlist] = useRemoveFromWishlistMutation();

  const isInWishlist = useMemo(() => (wishlist || []).includes(hit.id), [wishlist, hit.id]);

  const handleWishlistClick = useCallback(() => {
    if (isWishlistLoading) {
      return;
    }
    if (sessionInfo && !sessionInfo.isGuest) {
      if (isInWishlist) {
        removeFromWishlist({ elementId: hit.id, sessionInfo });
      } else {
        addToWishlist({ elementId: hit.id, sessionInfo });
      }
    } else {
      window.location = Craft.getSiteUrl('login', { redirect: window.location.href });
    }
  }, [
    isWishlistLoading,
    hit.id,
    sessionInfo,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
  ]);

  const productImages = (hit.productImages || [])
    .filter(({ notThumbnail }: { notThumbnail: boolean }) => !notThumbnail);
  const productImage = productImages.length > 1 ? productImages[1] : productImages[0];

  const position = results.hits.findIndex((result) => result.id === hit.id);
  const showUsps = additionalData.usps && position === 5;

  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const isNew = new Date(hit.timestamp * 1000) > oneMonthAgo;

  return (
    <>
      <Component
        title={hit.title}
        url={hit.url}
        image={productImage}
        priceFormatted={hit.priceFormatted}
        salePriceFormatted={hit.salePriceFormatted}
        isOnSale={!!hit.isOnSale}
        isNew={isNew}
        isOutOfStock={!!hit.showTempOutOfStockLabel}
        productCode={hit.productCode}
        defaultPrice={hit.defaultPrice}
        listId={additionalData.currentCategoryId}
        listName={additionalData.currentCategoryList}
        isInWishlist={isInWishlist}
        onWishlistClick={handleWishlistClick}
        onVisible={() => {
          if (additionalData.onProductVisible) {
            additionalData.onProductVisible({
              id: hit.id,
              title: hit.title,
              productCode: hit.productCode,
              defaultPrice: hit.defaultPrice,
            });
          }
        }}
      />
      {showUsps && (<Usps swiperSlides={additionalData.usps} />)}
    </>
  );
}

export default function HitWithWishlist({
  results,
  hit,
  additionalData,
  component,
}: componentTypes.HitProps & { component: React.ComponentType<ProductHitProps> }) {
  return (
    <Provider store={store}>
      <HitInner
        results={results}
        hit={hit}
        additionalData={additionalData}
        component={component}
      />
    </Provider>
  );
}
